import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  MessageSquare,
  Plus,
  Trash2,
  ListOrdered,
  Tag,
  Loader2,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import DashboardFooter from "@/components/DashboardFooter";
import WarmUpTagPickerModal from "@/components/WarmUpTagPickerModal";
import WarmUpWorkflowSetup from "@/pages/WarmUpSequences/WarmUpWorkflowSetup";
import {
  WarmUpAcceptanceNote,
  WarmUpInboundWorkflowLine,
  WarmUpTestModeLine,
} from "@/pages/WarmUpSequences/WarmUpDeliveryStatus";
import {
  createEmptyWarmUpMessage,
  normalizeWarmUpMessages,
  loadLegacyWarmUpDraftFromLocalStorage,
  clearWarmUpLocalStorageDraft,
  getWarmUpSequencesApiUrl,
  normalizeWarmUpPipelineCatalog,
  readWarmUpTriggerDraft,
  resolveWarmUpPitReadiness,
  warmUpIntegrationLocationSaved,
  warmUpTestModeActive,
  describeWarmUpCatalogMismatch,
  describeWarmUpTriggerAutomation,
  describeWarmUpPageHeadline,
  formatWarmUpSavedAt,
  readWarmUpValidationMessage,
  warmUpSaveIsRevisionConflict,
  retainWarmUpSavedStage,
  WARM_UP_SAME_PAIR_MESSAGE,
  warmUpDisplayedTriggerIds,
  warmUpEnableTriggerPayload,
  warmUpStagePairsMatch,
  warmUpValidationTargetsSamePair,
  WARM_UP_IMMEDIATE_DELAY_TEXT,
  formatWarmUpMessageDelayLabel,
  warmUpDelayIsImmediate,
  warmUpEmptyTextError,
} from "@/constants/warmUpSequenceDefaults";
import {
  WARM_UP_MERGE_TAGS,
  buildDynamicQualificationTags,
  mergeWarmUpTags,
  filterTagsByMessageType,
  filterTagsForWarmUpPicker,
} from "@/constants/warmUpMergeTags";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

function currentBackendUrl() {
  return process.env.REACT_APP_BACKEND_URL || "";
}

const ToggleSwitch = ({ enabled, onChange, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#00D1FF]/50 ${
      enabled ? "bg-[#00D1FF]" : "bg-white/10"
    }`}
    data-testid={testId}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

function WarmUpPitStatus({
  kind,
  triggerText,
  headline,
  catalogStatus,
  persistedConfig,
  persistedSavedAt,
  persistedTriggerStatus,
  sequencesLoading,
  testModeActive = false,
}) {
  const checking = kind === "loading";
  const pitReady = kind === "ready";
  let summary = "Checking the GoHighLevel connection.";
  if (kind === "placeholder") {
    summary = "A GoHighLevel record exists, but it is not an active PIT connection.";
  } else if (kind === "marketplace_only") {
    summary = "A Marketplace connection is not a Warm Up PIT connection.";
  } else if (kind === "incomplete") {
    summary = "PIT connection is incomplete. A PIT API key and location are both required.";
  } else if (kind === "missing") {
    summary = "GoHighLevel is not connected.";
  } else if (pitReady) {
    summary = "PIT connection is active.";
  }

  const selectorsText = pitReady
    ? "Pipeline selectors are available."
    : "Pipeline selectors are unavailable.";
  const sendingText = testModeActive
    ? "Sending is paused."
    : pitReady
      ? "Sending is available."
      : "Sending is unavailable.";
  const showEditor = !checking;
  const showCatalog = !pitReady && !checking;
  const showTrigger = Boolean(triggerText);
  const showRestore = !checking && !pitReady;
  const pitInSummary = !headline;
  const selectorsInSummary = selectorsText === "Pipeline selectors are unavailable.";
  const sendingInSummary = sendingText !== "Sending is available.";
  const triggerInSummary = triggerText !== "Automatic stage triggers are ready.";
  const showPointer =
    pitReady && catalogStatus === "ready" && triggerText === "Automatic stage triggers are not ready.";
  const formattedSavedAt = formatWarmUpSavedAt(persistedSavedAt);
  const showLastSaved = !sequencesLoading && Boolean(persistedTriggerStatus);
  const testModeInSummary = Boolean(testModeActive) || checking;
  const showDetails =
    showEditor ||
    !pitInSummary ||
    !selectorsInSummary ||
    !sendingInSummary ||
    !testModeInSummary ||
    (showTrigger && !triggerInSummary) ||
    Boolean(persistedConfig);

  const pitSummary = (
    <p className="text-[#F3F4F6] text-sm font-body" data-testid="warm-up-pit-summary">
      {summary}
    </p>
  );
  const editorNode = (
    <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-editor-availability">
      Message editing stays available.
    </p>
  );
  const selectorsNode = (
    <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-selectors-availability">
      {selectorsText}
    </p>
  );
  const sendingNode = (
    <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-sending-availability">
      {sendingText}
    </p>
  );
  const catalogNode = (
    <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-catalog-availability">
      Pipeline catalog is unavailable until a PIT API key and location are saved.
    </p>
  );
  const triggerNode = (
    <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-trigger-readiness">
      {triggerText}
    </p>
  );
  const testModeNode = (
    <WarmUpTestModeLine checking={checking} testModeActive={testModeActive} />
  );

  return (
    <section
      className={`rounded-2xl border p-4 space-y-2 ${
        pitReady ? "border-[#01B47A]/30 bg-[#01B47A]/5" : "border-white/10 bg-white/5"
      }`}
      data-testid="warm-up-pit-status"
    >
      <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
        Status
      </h2>
      {headline ? (
        <p className="text-[#F3F4F6] text-sm font-body" data-testid="warm-up-status-headline">
          {headline}
        </p>
      ) : null}
      {pitInSummary ? pitSummary : null}
      {selectorsInSummary ? selectorsNode : null}
      {sendingInSummary ? sendingNode : null}
      {showCatalog ? catalogNode : null}
      {testModeInSummary ? testModeNode : null}
      {showTrigger && triggerInSummary ? triggerNode : null}
      {showPointer ? (
        <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-trigger-setup-pointer">
          Set up the HighLevel workflow below.
        </p>
      ) : null}
      {showLastSaved ? (
        <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-last-saved">
          {formattedSavedAt
            ? `Trigger settings saved: ${formattedSavedAt}. Applies only to events received after this save.`
            : "Applies only to events received after this save."}
        </p>
      ) : null}
      {showRestore ? (
        <Link
          to="/dashboard/integrations/crm/ghl"
          className="inline-flex text-sm font-body text-[#00D1FF] hover:underline"
          data-testid="warm-up-restore-pit"
        >
          Restore PIT connection
        </Link>
      ) : null}
      {showDetails ? (
        <details className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <summary className="cursor-pointer text-sm text-[#9CA3AF] font-body select-none">
            Details
          </summary>
          <div className="mt-3">
            {showEditor ? editorNode : null}
            {pitInSummary ? null : pitSummary}
            {selectorsInSummary ? null : selectorsNode}
            {sendingInSummary ? null : sendingNode}
            {testModeInSummary ? null : testModeNode}
            {showTrigger && !triggerInSummary ? triggerNode : null}
            {persistedConfig ? (
              <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-persisted-config">
                {persistedConfig}
              </p>
            ) : null}
          </div>
        </details>
      ) : null}
    </section>
  );
}

const WARM_UP_SAVE_SCOPE_TEXT =
  "Save stores the messages. If Turn on or Turn off was changed in this visit and the pipeline list is loaded, Save stores those stage triggers too.";

function WarmUpAutomaticStartStop({
  catalogStatus,
  pipelines,
  onRetryCatalog,
  enablePipelineId,
  onEnablePipelineChange,
  enableStageId,
  onEnableStageChange,
  disablePipelineId,
  onDisablePipelineChange,
  disableStageId,
  onDisableStageChange,
  pairError,
  enableStaleText,
  disableStaleText,
  handleSave,
  isDirty,
  isSaving,
  sequencesLoading,
  pitKind,
  pitIntegrationId,
  locationSaved,
}) {
  const selectedEnablePipeline =
    catalogStatus === "ready"
      ? pipelines.find((pipeline) => pipeline.id === enablePipelineId) || null
      : null;
  const enableStageChoices = selectedEnablePipeline?.stages || [];
  const selectedDisablePipeline =
    catalogStatus === "ready"
      ? pipelines.find((pipeline) => pipeline.id === disablePipelineId) || null
      : null;
  const disableStageChoices = selectedDisablePipeline?.stages || [];

  return (
    <section
      className="glass rounded-2xl p-6 border border-white/10 space-y-4"
      data-testid="warm-up-automatic-start-stop"
    >
      <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
        Automatic start and stop
      </h2>
      <p className="text-[#9CA3AF] text-sm font-body">
        Turn Warm Up on and off automatically when an opportunity moves to a specific pipeline stage.
      </p>
      {catalogStatus !== "ready" && (
        <WarmUpPipelineCatalog
          status={catalogStatus}
          pipelines={pipelines}
          onRetry={onRetryCatalog}
        />
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={sequencesLoading || isSaving || !isDirty}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-heading font-bold transition-colors duration-300 ${
          !sequencesLoading && !isSaving && isDirty
            ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
            : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
        }`}
        data-testid="warm-up-save-stage-triggers"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          "Save"
        )}
      </button>
      <p className="text-[#9CA3AF] text-xs font-body">{WARM_UP_SAVE_SCOPE_TEXT}</p>
      {enableStaleText && (
        <p className="text-[#FBBF24] text-xs font-body" role="status" data-testid="warm-up-enable-stale">
          {enableStaleText}
        </p>
      )}
      {catalogStatus === "ready" && (
        <>
          <p className="text-[#F3F4F6] text-sm font-body font-semibold">Turn Warm Up on when moved to</p>
          <label className="block" htmlFor="warm-up-enable-pipeline">
            <span className="block text-[#9CA3AF] text-xs font-body mb-1">Pipeline</span>
            <select
              id="warm-up-enable-pipeline"
              value={pipelines.some((pipeline) => pipeline.id === enablePipelineId) ? enablePipelineId : ""}
              onChange={(event) => onEnablePipelineChange(event.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
              data-testid="warm-up-enable-pipeline"
            >
              <option value="">Select a pipeline</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name || pipeline.id}
                </option>
              ))}
            </select>
          </label>
          {selectedEnablePipeline && (
            <label className="block" htmlFor="warm-up-enable-stage">
              <span className="block text-[#9CA3AF] text-xs font-body mb-1">Stage</span>
              <select
                id="warm-up-enable-stage"
                value={
                  enableStageChoices.some((stage) => stage.id === enableStageId) ? enableStageId : ""
                }
                onChange={(event) => onEnableStageChange(event.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
                data-testid="warm-up-enable-stage"
              >
                <option value="">Select a stage</option>
                {enableStageChoices.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name || stage.id}
                  </option>
                ))}
              </select>
            </label>
          )}
          {pairError && (
            <p className="text-red-400 text-xs font-body" role="alert" data-testid="warm-up-enable-pair-error">
              {pairError}
            </p>
          )}
        </>
      )}
      {disableStaleText && (
        <p className="text-[#FBBF24] text-xs font-body" role="status" data-testid="warm-up-disable-stale">
          {disableStaleText}
        </p>
      )}
      {catalogStatus === "ready" && (
        <>
          <p className="text-[#F3F4F6] text-sm font-body font-semibold">Turn Warm Up off when moved to</p>
          <label className="block" htmlFor="warm-up-disable-pipeline">
            <span className="block text-[#9CA3AF] text-xs font-body mb-1">Pipeline</span>
            <select
              id="warm-up-disable-pipeline"
              value={
                pipelines.some((pipeline) => pipeline.id === disablePipelineId) ? disablePipelineId : ""
              }
              onChange={(event) => onDisablePipelineChange(event.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
              data-testid="warm-up-disable-pipeline"
            >
              <option value="">Select a pipeline</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name || pipeline.id}
                </option>
              ))}
            </select>
          </label>
          {selectedDisablePipeline && (
            <label className="block" htmlFor="warm-up-disable-stage">
              <span className="block text-[#9CA3AF] text-xs font-body mb-1">Stage</span>
              <select
                id="warm-up-disable-stage"
                value={
                  disableStageChoices.some((stage) => stage.id === disableStageId) ? disableStageId : ""
                }
                onChange={(event) => onDisableStageChange(event.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
                data-testid="warm-up-disable-stage"
              >
                <option value="">Select a stage</option>
                {disableStageChoices.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name || stage.id}
                  </option>
                ))}
              </select>
            </label>
          )}
          {pairError && (
            <p className="text-red-400 text-xs font-body" role="alert" data-testid="warm-up-disable-pair-error">
              {pairError}
            </p>
          )}
        </>
      )}
      <p className="text-[#9CA3AF] text-sm font-body">
        Only an exact match of the selected pipeline and stage changes Warm Up.
      </p>
      <WarmUpInboundWorkflowLine
        checking={pitKind === "loading"}
        integrationPresent={Boolean(pitIntegrationId)}
        locationSaved={locationSaved}
      />
      {catalogStatus === "ready" && (
        <details className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <summary className="cursor-pointer text-sm text-[#9CA3AF] font-body select-none">
            Pipeline and stage IDs
          </summary>
          <div className="mt-3">
            <WarmUpPipelineCatalog status="ready" pipelines={pipelines} onRetry={onRetryCatalog} />
          </div>
        </details>
      )}
    </section>
  );
}

function WarmUpPipelineCatalog({ status, pipelines, onRetry }) {
  if (status === "loading") {
    return (
      <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-catalog-loading">
        Loading pipelines from GoHighLevel.
      </p>
    );
  }
  if (status === "error") {
    return (
      <div className="space-y-2" data-testid="warm-up-catalog-error">
        <p className="text-[#9CA3AF] text-sm font-body">
          Pipeline catalog is temporarily unavailable. No substitute pipelines were used.
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-body text-[#00D1FF] hover:underline"
          data-testid="warm-up-catalog-retry"
        >
          Try again
        </button>
      </div>
    );
  }
  if (status === "empty") {
    return (
      <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-catalog-empty">
        GoHighLevel returned no pipelines for this location.
      </p>
    );
  }
  if (status !== "ready") return null;
  return (
    <ul className="space-y-3" data-testid="warm-up-catalog-list">
      {pipelines.map((pipeline) => (
        <li key={pipeline.id} data-testid={`warm-up-catalog-pipeline-${pipeline.id}`}>
          <p className="text-[#F3F4F6] text-sm font-body">
            {pipeline.name} <span className="text-[#9CA3AF]">{pipeline.id}</span>
          </p>
          <ul className="mt-1 space-y-1 pl-4">
            {pipeline.stages.map((stage) => (
              <li
                key={stage.id}
                className="text-[#9CA3AF] text-sm font-body"
                data-testid={`warm-up-catalog-stage-${pipeline.id}-${stage.id}`}
              >
                {stage.name} {stage.id}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

const tagButtonClassName =
  "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-body text-[#00D1FF] border border-[#00D1FF]/30 bg-[#00D1FF]/10 hover:bg-[#00D1FF]/20 transition-colors";

function useNonNegativeDelayInput(messageId, value) {
  const [input, setInput] = useState(() => String(value ?? 0));
  useEffect(() => {
    setInput(String(value ?? 0));
  }, [messageId, value]);
  return [input, setInput];
}

const SequenceMessageCard = ({
  index,
  message,
  onChange,
  onRemove,
  testIdPrefix,
  onOpenTagPicker,
  textError = "",
}) => {
  const [delayInput, setDelayInput] = useNonNegativeDelayInput(message.id, message.delay_days);
  const [hoursInput, setHoursInput] = useNonNegativeDelayInput(message.id, message.delay_hours);
  const [minutesInput, setMinutesInput] = useNonNegativeDelayInput(
    message.id,
    message.delay_minutes
  );

  const applyDelayPart = (field, setInput) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setInput("");
      onChange({ ...message, [field]: 0 });
      return;
    }
    const digits = raw.replace(/\D/g, "");
    if (digits === "") return;
    const num = Math.max(0, parseInt(digits, 10));
    setInput(String(num));
    onChange({ ...message, [field]: num });
  };

  const blurDelayPart = (field, input, setInput) => () => {
    if (input === "") {
      setInput("0");
      onChange({ ...message, [field]: 0 });
    }
  };

  const handleDelayFocus = (e) => e.target.select();

  return (
    <div
      className={`p-4 rounded-lg bg-white/5 border border-white/10 space-y-4 transition-opacity ${
        message.enabled ? "" : "opacity-60"
      }`}
      data-testid={testIdPrefix}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-[#F3F4F6] text-sm font-body font-semibold">
          Message {index + 1}
          <span className="text-[#9CA3AF] text-xs font-body" data-testid={`${testIdPrefix}-delay-label`}>
            {` · ${formatWarmUpMessageDelayLabel(message)}`}
          </span>
        </h3>
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-[#9CA3AF] text-xs font-body">Enabled</span>
          <ToggleSwitch
            enabled={!!message.enabled}
            onChange={(v) => onChange({ ...message, enabled: v })}
            testId={`${testIdPrefix}-enabled`}
          />
          <button
            type="button"
            onClick={onRemove}
            className="p-2 rounded-lg text-[#9CA3AF] hover:text-red-400 hover:bg-red-500/10 transition-colors"
            aria-label="Remove message"
            data-testid={`${testIdPrefix}-remove`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <label className="text-[#9CA3AF] text-xs font-body">Message text</label>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={onOpenTagPicker}
              className={tagButtonClassName}
              aria-label="Add merge tag"
              data-testid={`${testIdPrefix}-add-tag`}
            >
              <Tag className="w-3.5 h-3.5" />
              Add tag
            </button>
          </div>
        </div>
        <textarea
          rows={4}
          value={message.text}
          onChange={(e) => onChange({ ...message, text: e.target.value })}
          placeholder="Message text for the lead…"
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 resize-y min-h-[96px]"
          data-testid={`${testIdPrefix}-text`}
          aria-invalid={textError ? "true" : "false"}
        />
        {textError && (
          <p className="text-red-400 text-xs font-body mt-1" data-testid={`${testIdPrefix}-text-error`} role="alert">
            {textError}
          </p>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <div className="min-w-0">
          <p className="text-[#F3F4F6] text-xs font-body font-semibold">AI corrected</p>
          <p className="text-[#9CA3AF] text-xs font-body mt-1 leading-snug">
            Rewrites this message to sound natural for each lead before sending.
          </p>
        </div>
        <ToggleSwitch
          enabled={!!message.ai_corrected}
          onChange={(v) => onChange({ ...message, ai_corrected: v })}
          testId={`${testIdPrefix}-ai-corrected`}
        />
      </div>

      <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <span className="block text-[#9CA3AF] text-xs font-body mb-1">Send after</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[#9CA3AF] text-xs font-body mb-1" htmlFor={`${testIdPrefix}-delay`}>
              Days
            </label>
            <input
              id={`${testIdPrefix}-delay`}
              type="text"
              inputMode="numeric"
              value={delayInput}
              onChange={applyDelayPart("delay_days", setDelayInput)}
              onBlur={blurDelayPart("delay_days", delayInput, setDelayInput)}
              onFocus={handleDelayFocus}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
              data-testid={`${testIdPrefix}-delay`}
            />
          </div>
          <div>
            <label className="block text-[#9CA3AF] text-xs font-body mb-1" htmlFor={`${testIdPrefix}-delay-hours`}>
              Hours
            </label>
            <input
              id={`${testIdPrefix}-delay-hours`}
              type="text"
              inputMode="numeric"
              value={hoursInput}
              onChange={applyDelayPart("delay_hours", setHoursInput)}
              onBlur={blurDelayPart("delay_hours", hoursInput, setHoursInput)}
              onFocus={handleDelayFocus}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
              data-testid={`${testIdPrefix}-delay-hours`}
            />
          </div>
          <div>
            <label className="block text-[#9CA3AF] text-xs font-body mb-1" htmlFor={`${testIdPrefix}-delay-minutes`}>
              Minutes
            </label>
            <input
              id={`${testIdPrefix}-delay-minutes`}
              type="text"
              inputMode="numeric"
              value={minutesInput}
              onChange={applyDelayPart("delay_minutes", setMinutesInput)}
              onBlur={blurDelayPart("delay_minutes", minutesInput, setMinutesInput)}
              onFocus={handleDelayFocus}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
              data-testid={`${testIdPrefix}-delay-minutes`}
            />
          </div>
        </div>
        {warmUpDelayIsImmediate(message) && (
          <p
            className="text-[#F3F4F6] text-xs font-body leading-snug"
            data-testid={`${testIdPrefix}-delay-immediate`}
          >
            {WARM_UP_IMMEDIATE_DELAY_TEXT}
          </p>
        )}
      </div>
    </div>
  );
};

const WarmUpSequences = () => {
  const [messages, setMessages] = useState([createEmptyWarmUpMessage()]);
  const [sequencesLoading, setSequencesLoading] = useState(true);
  const [pitKind, setPitKind] = useState("loading");
  const [catalogStatus, setCatalogStatus] = useState("unavailable");
  const [pipelines, setPipelines] = useState([]);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [pitIntegrationId, setPitIntegrationId] = useState("");
  const [locationSaved, setLocationSaved] = useState(false);
  const [webhookSecretConfigured, setWebhookSecretConfigured] = useState(false);
  const [testModeActive, setTestModeActive] = useState(false);
  const [enablePipelineId, setEnablePipelineId] = useState("");
  const [enableStage, setEnableStage] = useState({ id: "", name: "" });
  const [disablePipelineId, setDisablePipelineId] = useState("");
  const [disableStage, setDisableStage] = useState({ id: "", name: "" });
  const [enableSelectionDirty, setEnableSelectionDirty] = useState(false);
  const [disableSelectionDirty, setDisableSelectionDirty] = useState(false);
  const [messageConfigRevision, setMessageConfigRevision] = useState(0);
  const [triggerConfigRevision, setTriggerConfigRevision] = useState(0);
  const [persistedTriggerStatus, setPersistedTriggerStatus] = useState("");
  const [persistedSavedAt, setPersistedSavedAt] = useState("");
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [revisionConflict, setRevisionConflict] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [storedMessages, setStoredMessages] = useState([]);
  const [textValidationVisible, setTextValidationVisible] = useState(false);

  const [tagModalContext, setTagModalContext] = useState(null);
  const [dynamicQualTags, setDynamicQualTags] = useState([]);
  const [botSettings, setBotSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const allMergeTags = useMemo(
    () => mergeWarmUpTags(WARM_UP_MERGE_TAGS, dynamicQualTags),
    [dynamicQualTags]
  );

  const applyTriggerDraft = (sequences) => {
    const draft = readWarmUpTriggerDraft(sequences);
    setEnablePipelineId(draft.pipelineId);
    setEnableStage(draft.stage);
    setDisablePipelineId(draft.disablePipelineId);
    setDisableStage(draft.disableStage);
    setMessageConfigRevision(draft.messageRevision);
    setTriggerConfigRevision(draft.triggerRevision);
    setPersistedTriggerStatus(draft.triggerStatus);
    setPersistedSavedAt(draft.savedAt);
    setEnableSelectionDirty(false);
    setDisableSelectionDirty(false);
    if (Array.isArray(sequences?.messages)) {
      const loaded = normalizeWarmUpMessages(sequences.messages);
      setStoredMessages(loaded);
      setMessages(loaded.length > 0 ? loaded : [createEmptyWarmUpMessage()]);
    }
  };

  useEffect(() => {
    const token = getClientAuthToken();
    const backendUrl = currentBackendUrl();
    if (!token || !backendUrl) {
      setSequencesLoading(false);
      setMessages([createEmptyWarmUpMessage()]);
      applyTriggerDraft(null);
      setIsDirty(false);
      return;
    }

    const sequencesUrl = getWarmUpSequencesApiUrl(backendUrl);
    let cancelled = false;
    (async () => {
      setSequencesLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(sequencesUrl, { headers });
        if (cancelled) return;

        const sequences = res.data?.has_sequences ? res.data.sequences : null;
        applyTriggerDraft(sequences);
        if (Array.isArray(sequences?.messages)) {
          clearWarmUpLocalStorageDraft();
          setIsDirty(false);
          return;
        }

        const legacy = loadLegacyWarmUpDraftFromLocalStorage();
        if (legacy && legacy.length > 0) {
          if (legacy.some((message) => warmUpEmptyTextError([], message))) {
            setMessages(legacy);
            setTextValidationVisible(true);
            setIsDirty(true);
            return;
          }
          try {
            const migrated = await axios.put(
              sequencesUrl,
              { messages: legacy, expected_message_config_revision: 0 },
              { headers }
            );
            if (cancelled) return;
            const saved = migrated.data?.sequences;
            if (!saved || typeof saved !== "object" || !Array.isArray(saved.messages)) {
              setMessages(legacy);
              setIsDirty(true);
              return;
            }
            applyTriggerDraft(saved);
            clearWarmUpLocalStorageDraft();
            setIsDirty(false);
          } catch {
            if (!cancelled) {
              setMessages(legacy);
              setIsDirty(true);
            }
          }
          return;
        }

        setMessages([createEmptyWarmUpMessage()]);
        setIsDirty(false);
      } catch {
        if (!cancelled) {
          setMessages([createEmptyWarmUpMessage()]);
          applyTriggerDraft(null);
          setIsDirty(false);
        }
      } finally {
        if (!cancelled) setSequencesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (saveState !== "saved") return undefined;
    const t = setTimeout(() => setSaveState("idle"), 2000);
    return () => clearTimeout(t);
  }, [saveState]);

  useEffect(() => {
    if (!warmUpValidationTargetsSamePair(saveError)) return;
    if (
      warmUpStagePairsMatch({
        enablePipelineId,
        enableStageId: enableStage.id,
        disablePipelineId,
        disableStageId: disableStage.id,
      })
    ) {
      return;
    }
    setSaveError("");
    setSaveState((state) => (state === "error" ? "idle" : state));
  }, [saveError, enablePipelineId, enableStage.id, disablePipelineId, disableStage.id]);

  useEffect(() => {
    const token = getClientAuthToken();
    const backendUrl = currentBackendUrl();
    if (!token || !backendUrl) {
      setPitKind("missing");
      setLocationSaved(false);
      setWebhookSecretConfigured(false);
      setTestModeActive(false);
      setPitIntegrationId("");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/integrations/ghl`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setPitKind(resolveWarmUpPitReadiness(res.data).kind);
        setPitIntegrationId(typeof res.data?.id === "string" ? res.data.id.trim() : "");
        setLocationSaved(warmUpIntegrationLocationSaved(res.data));
        setWebhookSecretConfigured(res.data?.has_warm_up_webhook_secret === true);
        setTestModeActive(warmUpTestModeActive(res.data));
      } catch {
        if (!cancelled) {
          setPitKind("missing");
          setPitIntegrationId("");
          setLocationSaved(false);
          setWebhookSecretConfigured(false);
          setTestModeActive(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (pitKind !== "ready") {
      setCatalogStatus("unavailable");
      setPipelines([]);
      return undefined;
    }
    const token = getClientAuthToken();
    const backendUrl = currentBackendUrl();
    if (!token || !backendUrl) {
      setCatalogStatus("unavailable");
      setPipelines([]);
      return undefined;
    }

    let cancelled = false;
    setCatalogStatus("loading");
    setPipelines([]);
    (async () => {
      try {
        const res = await axios.post(
          `${backendUrl}/api/integrations/ghl/list_pipelines`,
          null,
          { headers: getClientAuthHeaders() }
        );
        if (cancelled) return;
        const loaded = normalizeWarmUpPipelineCatalog(res.data);
        if (!loaded) {
          setCatalogStatus("error");
          setPipelines([]);
          return;
        }
        setPipelines(loaded);
        setCatalogStatus(loaded.length === 0 ? "empty" : "ready");
      } catch {
        if (!cancelled) {
          setCatalogStatus("error");
          setPipelines([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pitKind, catalogAttempt]);

  useEffect(() => {
    if (catalogStatus !== "ready") return;
    setEnableStage((current) => retainWarmUpSavedStage(pipelines, enablePipelineId, current));
  }, [catalogStatus, pipelines, enablePipelineId]);

  useEffect(() => {
    if (catalogStatus !== "ready") return;
    setDisableStage((current) => retainWarmUpSavedStage(pipelines, disablePipelineId, current));
  }, [catalogStatus, pipelines, disablePipelineId]);

  useEffect(() => {
    const token = getClientAuthToken();
    const backendUrl = currentBackendUrl();
    if (!token || !backendUrl) {
      setSettingsLoading(false);
      setBotSettings(null);
      setDynamicQualTags([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setSettingsLoading(true);
      try {
        const res = await axios.get(`${backendUrl}/api/bot/settings`, {
          headers: getClientAuthHeaders(),
        });
        if (cancelled) return;
        const settings = res.data?.settings ?? null;
        setBotSettings(settings);
        const questions = settings?.qualification_logic?.questions;
        setDynamicQualTags(buildDynamicQualificationTags(questions));
      } catch {
        if (!cancelled) {
          setBotSettings(null);
          setDynamicQualTags([]);
        }
      } finally {
        if (!cancelled) setSettingsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const markDirty = useCallback(() => {
    setIsDirty(true);
    setSaveState("idle");
  }, []);

  const handleEnablePipelineChange = useCallback((pipelineId) => {
    setEnablePipelineId(pipelineId);
    setEnableStage({ id: "", name: "" });
    setEnableSelectionDirty(true);
    markDirty();
  }, [markDirty]);

  const handleDisablePipelineChange = useCallback((pipelineId) => {
    setDisablePipelineId(pipelineId);
    setDisableStage({ id: "", name: "" });
    setDisableSelectionDirty(true);
    markDirty();
  }, [markDirty]);

  const handleDisableStageChange = useCallback((stageId) => {
    const pipeline = pipelines.find((item) => item.id === disablePipelineId);
    const match = (pipeline?.stages || []).find((item) => item.id === stageId);
    setDisableStage(
      match
        ? { id: match.id, name: typeof match.name === "string" ? match.name : "" }
        : { id: "", name: "" }
    );
    setDisableSelectionDirty(true);
    markDirty();
  }, [pipelines, disablePipelineId, markDirty]);

  const handleEnableStageChange = useCallback((stageId) => {
    const pipeline = pipelines.find((item) => item.id === enablePipelineId);
    const match = (pipeline?.stages || []).find((item) => item.id === stageId);
    setEnableStage(
      match
        ? { id: match.id, name: typeof match.name === "string" ? match.name : "" }
        : { id: "", name: "" }
    );
    setEnableSelectionDirty(true);
    markDirty();
  }, [pipelines, enablePipelineId, markDirty]);

  const handleSave = useCallback(async () => {
    const normalized = normalizeWarmUpMessages(messages);
    if (normalized.some((message) => warmUpEmptyTextError(storedMessages, message))) {
      setTextValidationVisible(true);
      return;
    }
    setTextValidationVisible(false);

    const token = getClientAuthToken();
    const backendUrl = currentBackendUrl();
    if (!token || !backendUrl) {
      setSaveState("error");
      return;
    }

    const payload = {
      messages: normalized,
      expected_message_config_revision: messageConfigRevision,
    };
    if (
      (enableSelectionDirty || disableSelectionDirty) &&
      catalogStatus === "ready" &&
      pitIntegrationId
    ) {
      const triggers = warmUpEnableTriggerPayload({
        integrationId: pitIntegrationId,
        pipelineId: enablePipelineId,
        pipelines,
        stage: enableStage,
        disablePipelineId,
        disableStage,
      });
      if (triggers) {
        payload.ghl_triggers = triggers;
        payload.expected_trigger_config_revision = triggerConfigRevision;
      }
    }

    setIsSaving(true);
    setSaveState("idle");
    setSaveError("");
    try {
      const res = await axios.put(
        getWarmUpSequencesApiUrl(backendUrl),
        payload,
        { headers: getClientAuthHeaders() }
      );
      const saved = res.data?.sequences;
      if (!saved || typeof saved !== "object") {
        setSaveState("error");
        return;
      }
      applyTriggerDraft(saved);
      setTextValidationVisible(false);
      setRevisionConflict(false);
      setIsDirty(false);
      setSaveError("");
      setSaveState("saved");
    } catch (error) {
      if (warmUpSaveIsRevisionConflict(error)) {
        setRevisionConflict(true);
        setSaveState("conflict");
        try {
          const loaded = await axios.get(getWarmUpSequencesApiUrl(backendUrl), {
            headers: getClientAuthHeaders(),
          });
          const sequences = loaded.data?.has_sequences ? loaded.data.sequences : null;
          if (!sequences || typeof sequences !== "object") {
            setSaveError("Could not reload the saved configuration.");
            setSaveState("error");
            return;
          }
          const persisted = readWarmUpTriggerDraft(sequences);
          setMessageConfigRevision(persisted.messageRevision);
          setTriggerConfigRevision(persisted.triggerRevision);
          setPersistedTriggerStatus(persisted.triggerStatus);
          setPersistedSavedAt(persisted.savedAt);
          setSaveError("");
        } catch {
          setSaveError("Could not reload the saved configuration.");
          setSaveState("error");
        }
        return;
      }
      const validationMessage = readWarmUpValidationMessage(error);
      setSaveError(validationMessage);
      setSaveState("error");
    } finally {
      setIsSaving(false);
    }
  }, [
    messages,
    enableSelectionDirty,
    catalogStatus,
    pitIntegrationId,
    enablePipelineId,
    pipelines,
    enableStage,
    disableSelectionDirty,
    disablePipelineId,
    disableStage,
    messageConfigRevision,
    triggerConfigRevision,
    storedMessages,
  ]);

  const addMessage = useCallback(() => {
    markDirty();
    setMessages((prev) => [...prev, createEmptyWarmUpMessage()]);
  }, [markDirty]);

  const updateMessage = useCallback((index, value) => {
    markDirty();
    setMessages((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, [markDirty]);

  const removeMessage = useCallback((index) => {
    markDirty();
    setMessages((prev) => prev.filter((_, i) => i !== index));
    setTagModalContext((ctx) => {
      if (!ctx) return null;
      if (ctx.messageIndex === index) return null;
      if (ctx.messageIndex > index) {
        return { ...ctx, messageIndex: ctx.messageIndex - 1 };
      }
      return ctx;
    });
  }, [markDirty]);

  const openTagPicker = useCallback((messageIndex) => {
    const msg = messages[messageIndex];
    if (!msg) return;
    setTagModalContext({
      messageIndex,
      messageType: "general",
    });
  }, [messages]);

  const modalMessageType = tagModalContext?.messageType ?? "general";
  const persistedConfig =
    sequencesLoading || !persistedTriggerStatus
      ? ""
      : `Saved trigger configuration is ${persistedTriggerStatus}, message revision ${messageConfigRevision}, trigger revision ${triggerConfigRevision}. It applies only to events received after this save.${
          persistedSavedAt ? ` Saved at ${persistedSavedAt}.` : ""
        }`;

  const displayedTriggerIds = warmUpDisplayedTriggerIds({
    catalogStatus,
    pipelines,
    enablePipelineId,
    enableStage,
    disablePipelineId,
    disableStage,
  });
  const triggerText = sequencesLoading
    ? ""
    : describeWarmUpTriggerAutomation(displayedTriggerIds);
  const headline = describeWarmUpPageHeadline({
    pitKind,
    sequencesLoading,
    testModeActive,
    triggerText,
  });
  const catalogComparable = catalogStatus === "ready" || catalogStatus === "empty";
  const enableStaleText =
    sequencesLoading || !catalogComparable
      ? ""
      : describeWarmUpCatalogMismatch({
          section: "enable",
          pipelines,
          pipelineId: enablePipelineId,
          stage: enableStage,
        });
  const disableStaleText =
    sequencesLoading || !catalogComparable
      ? ""
      : describeWarmUpCatalogMismatch({
          section: "disable",
          pipelines,
          pipelineId: disablePipelineId,
          stage: disableStage,
        });

  const modalTags = useMemo(
    () =>
      filterTagsForWarmUpPicker(
        filterTagsByMessageType(allMergeTags, modalMessageType)
      ),
    [allMergeTags, modalMessageType]
  );

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="warm-up-sequences-page">
      <Sidebar />
      <div className="flex-1 md:ml-64">
        <main className="pt-8 pb-16 px-6 max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2 flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-[#00D1FF]" />
              Warm-up Sequences
            </h1>
            <p className="text-[#9CA3AF] font-body text-sm leading-relaxed" data-testid="warm-up-sequence-schedule">
              Send a series of follow-up messages automatically after Warm Up is turned on for a chat. Warm Up can be turned on manually or by a pipeline stage change.
            </p>
          </div>

          <div className="mb-6">
            <WarmUpPitStatus
              kind={pitKind}
              triggerText={triggerText}
              headline={headline}
              catalogStatus={catalogStatus}
              persistedConfig={persistedConfig}
              persistedSavedAt={persistedSavedAt}
              persistedTriggerStatus={persistedTriggerStatus}
              sequencesLoading={sequencesLoading}
              testModeActive={testModeActive}
            />
          </div>

          <div className="mb-6">
          <div className="glass rounded-2xl p-6 border border-white/10 space-y-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-[#F3F4F6] font-heading font-semibold text-lg flex items-center gap-2">
                <ListOrdered className="w-5 h-5 text-[#00D1FF]" />
                Messages
              </h2>
              <div className="flex items-center gap-3 ml-auto">
                {!sequencesLoading && saveState === "saved" && (
                  <span className="text-[#01B47A] text-xs font-body">Saved</span>
                )}
                {!sequencesLoading && revisionConflict && (
                  <span className="text-red-400 text-xs font-body" data-testid="warm-up-revision-conflict">
                    The saved configuration changed. Your draft was kept and was not written over it. Save again to apply your draft.
                  </span>
                )}
                {!sequencesLoading && saveState === "error" && (
                  <span className="text-red-400 text-xs font-body" data-testid="warm-up-save-error">
                    {saveError || "Could not save"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={sequencesLoading || isSaving || !isDirty}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-heading font-bold transition-colors duration-300 ${
                    !sequencesLoading && !isSaving && isDirty
                      ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                  }`}
                  data-testid="warm-up-save-sequences"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
                <button
                  type="button"
                  onClick={addMessage}
                  disabled={sequencesLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00D1FF]/10 border border-[#00D1FF]/30 text-[#00D1FF] text-sm font-body hover:bg-[#00D1FF]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="warm-up-add-message"
                >
                  <Plus className="w-4 h-4" />
                  Add message
                </button>
              </div>
            </div>
            <p className="text-[#9CA3AF] text-xs font-body" data-testid="warm-up-save-scope">
              {WARM_UP_SAVE_SCOPE_TEXT}
            </p>

            {sequencesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#00D1FF] animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-[#9CA3AF] text-sm font-body text-center py-8">
                No messages yet. Click Add message to create your first warm-up step.
              </p>
            ) : (
              <>
              <p className="text-[#9CA3AF] text-sm font-body" data-testid="warm-up-delay-rule">
                Days, hours, and minutes add up. Each delay is counted from when Warm Up is turned on, not from the previous message.
              </p>
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <SequenceMessageCard
                    key={msg.id}
                    index={idx}
                    message={msg}
                    onChange={(v) => updateMessage(idx, v)}
                    onRemove={() => removeMessage(idx)}
                    testIdPrefix={`warm-up-message-${idx}`}
                    onOpenTagPicker={() => openTagPicker(idx)}
                    textError={
                      textValidationVisible ? warmUpEmptyTextError(storedMessages, msg) : ""
                    }
                  />
                ))}
              </div>
              </>
            )}

            {!sequencesLoading && messages.length === 0 && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={addMessage}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 text-[#F3F4F6] text-sm font-body hover:bg-white/5 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add message
                </button>
              </div>
            )}
            <WarmUpAcceptanceNote />
          </div>
          </div>

          <div className="mb-6">
            <WarmUpAutomaticStartStop
              catalogStatus={catalogStatus}
              pipelines={pipelines}
              onRetryCatalog={() => setCatalogAttempt((attempt) => attempt + 1)}
              enablePipelineId={enablePipelineId}
              onEnablePipelineChange={handleEnablePipelineChange}
              enableStageId={enableStage.id}
              onEnableStageChange={handleEnableStageChange}
              disablePipelineId={disablePipelineId}
              onDisablePipelineChange={handleDisablePipelineChange}
              disableStageId={disableStage.id}
              onDisableStageChange={handleDisableStageChange}
              pairError={
                sequencesLoading || catalogStatus !== "ready"
                  ? ""
                  : warmUpValidationTargetsSamePair(saveError)
                    ? saveError
                    : warmUpStagePairsMatch(displayedTriggerIds)
                      ? WARM_UP_SAME_PAIR_MESSAGE
                      : ""
              }
              enableStaleText={enableStaleText}
              disableStaleText={disableStaleText}
              handleSave={handleSave}
              isDirty={isDirty}
              isSaving={isSaving}
              sequencesLoading={sequencesLoading}
              pitKind={pitKind}
              pitIntegrationId={pitIntegrationId}
              locationSaved={locationSaved}
            />
          </div>

          <WarmUpWorkflowSetup
            integrationId={pitIntegrationId}
            onSecretConfigured={() => setWebhookSecretConfigured(true)}
            sequencesLoading={sequencesLoading}
            triggerText={triggerText}
            webhookSecretConfigured={webhookSecretConfigured}
            pitKind={pitKind}
            catalogStatus={catalogStatus}
          />
        </main>
        <DashboardFooter />
      </div>

      <WarmUpTagPickerModal
        open={tagModalContext !== null}
        onClose={() => setTagModalContext(null)}
        messageType={modalMessageType}
        tags={modalTags}
        botSettings={botSettings}
        settingsLoading={settingsLoading}
      />
    </div>
  );
};

export default WarmUpSequences;
